import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardBody, Col, Button, Spinner, Row } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";
import { useAuth } from "../../../Context/AuthContext";

interface CashFlow {
  id: string;
  date: string;
  received_amount: string;
  total_expense: string;
  received_amount_without_currency: number;
  total_expense_without_currency: number;
  isFooter?: boolean;
}

const customStyles = {
  headCells: {
    style: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#308e87",
      backgroundColor: "#f8f9fa",
      borderBottom: "2px solid #308e87",
    },
  },
  rows: { style: { borderBottom: "1px solid #dee2e6" } },
  table: { style: { border: "1px solid #dee2e6", borderRadius: "8px" } },
};

const conditionalRowStyles = [
  {
    when: (row: any) => row.isFooter === true,
    style: {
      fontWeight: "800",
      backgroundColor: "#f8f9fa",
      color: "#308e87",
      borderTop: "2px solid #dee2e6",
      fontSize: "16px",
    },
  },
];

const formatDate = (date: Date | null): string | null => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = `0${date.getMonth() + 1}`.slice(-2);
  const d = `0${date.getDate()}`.slice(-2);
  return `${y}-${m}-${d}`;
};

export default function CashFlowReport() {
  const token = localStorage.getItem("token");
  const { currency } = useCurrency();
  const { hasPermission } = useAuth();

  const [filteredItems, setFilteredItems] = useState<CashFlow[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [grandTotals, setGrandTotals] = useState({
    received_amount: 0,
    total_expense: 0,
  });

  const fetchData = useCallback(
    async (page = currentPage, pp = perPage) => {
      if (fromDate && toDate && fromDate > toDate) {
        toast.error("From date cannot be greater than To date.");
        return;
      }
      setTableLoading(true);
      try {
        const res = await axios.get(`${baseUrl}call_history/getDailyFinancialReport`, {
          params: {
            page,
            per_page: pp,
            from_date: formatDate(fromDate),
            to_date: formatDate(toDate),
            sort_field: sortField,
            sort_direction: sortDir,
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        setFilteredItems(res.data.data || []);
        setTotalRows(res.data.total || 0);
        setCurrentPage(res.data.current_page || 1);
        if (res.data.grand_totals) {
          setGrandTotals({
            received_amount: res.data.grand_totals.received_amount || 0,
            total_expense: res.data.grand_totals.total_expense || 0,
          });
        }
      } catch (e) {
        console.error("Error fetching cash flow data:", e);
      } finally {
        setTableLoading(false);
      }
    },
    [currentPage, perPage, fromDate, toDate, sortField, sortDir, token]
  );

  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [currentPage, perPage, fromDate, toDate, sortField, sortDir]);

  const handleSort = useCallback((column: any, direction: "asc" | "desc") => {
    setSortField(column.sortField || "date");
    setSortDir(direction);
    setCurrentPage(1);
  }, []);

  const fetchAllForExport = async (): Promise<CashFlow[]> => {
    try {
      const res = await axios.get(`${baseUrl}call_history/getDailyFinancialReportNoPagination`, {
        params: {
          from_date: formatDate(fromDate),
          to_date: formatDate(toDate),
          sort_field: sortField,
          sort_direction: sortDir,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      return Array.isArray(res.data) ? res.data : res.data.data || [];
    } catch {
      return [];
    }
  };

  const columnsData = useMemo<CashFlow[]>(() => {
    if (filteredItems.length === 0) return [];
    const totalPages = Math.ceil(totalRows / perPage) || 1;
    const isLastPage = currentPage >= totalPages;
    if (!isLastPage) return filteredItems;
    return [
      ...filteredItems,
      {
        isFooter: true,
        id: "footer",
        date: "Total",
        received_amount: "",
        total_expense: "",
        received_amount_without_currency: grandTotals.received_amount,
        total_expense_without_currency: grandTotals.total_expense,
      },
    ];
  }, [filteredItems, grandTotals, currentPage, perPage, totalRows]);

  const currencyH = currency ? ` (${currency})` : "";
  const columns = useMemo(() => [
    {
      name: <div>Sl.No</div>,
      cell: (row: CashFlow, index: number) => (row.isFooter ? "" : (currentPage - 1) * perPage + index + 1),
      width: "75px",
    },
    {
      name: <div>Date</div>,
      sortable: true,
      sortField: "date",
      cell: (row: CashFlow) => (row.isFooter ? <strong>Total</strong> : row.date),
    },
    {
      name: <div>Amount Received{currencyH}</div>,
      sortable: true,
      sortField: "received_amount",
      cell: (row: CashFlow) => (row.isFooter ? grandTotals.received_amount : row.received_amount_without_currency ?? "N/A"),
    },
    {
      name: <div>Total Expense{currencyH}</div>,
      sortable: true,
      sortField: "total_expense",
      cell: (row: CashFlow) => (row.isFooter ? grandTotals.total_expense : row.total_expense_without_currency ?? "N/A"),
    },
  ], [currency, currentPage, perPage, grandTotals]);

  const exportPDF = async () => {
    const allData = await fetchAllForExport();
    if (!allData.length) {
      toast.warn("No data for export.");
      return;
    }
    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      $: "USD",
      "€": "EUR",
      "£": "GBP",
    };
    const safeCurrency = currencyMap[currency] ?? currency;
    const safeCurrencyHeader = safeCurrency ? ` (${safeCurrency})` : "";

    let totalR = 0, totalE = 0;
    const rows = allData.map((row: any, i: number) => {
      totalR += Number(row.received_amount_without_currency) || 0;
      totalE += Number(row.total_expense_without_currency) || 0;
      return [i + 1, row.date || "N/A", String(row.received_amount_without_currency ?? "0"), String(row.total_expense_without_currency ?? "0")];
    });
    rows.push(["Total", "", String(totalR.toFixed(2)), String(totalE.toFixed(2))]);
    exportToPDF({
      fileName: "Cash_Flow_Report",
      columns: ["Sl.No", "Date", `Amount Received${safeCurrencyHeader}`, `Total Expense${safeCurrencyHeader}`],
      data: rows,
      emptyDataMessage: "No cash flow available for export.",
    });
  };

  const exportExcel = async () => {
    const allData = await fetchAllForExport();
    if (!allData.length) {
      toast.warn("No data for export.");
      return;
    }
    const currencyHeader = currency ? ` (${currency})` : "";
    let totalR = 0, totalE = 0;
    const tableData = allData.map((row: any, i: number) => {
      totalR += Number(row.received_amount_without_currency) || 0;
      totalE += Number(row.total_expense_without_currency) || 0;
      return {
        "Sl.No": i + 1,
        Date: row.date || "N/A",
        [`Amount Received${currencyHeader}`]: row.received_amount_without_currency ?? 0,
        [`Total Expense${currencyHeader}`]: row.total_expense_without_currency ?? 0,
      };
    });
    tableData.push({
      "Sl.No": "" as any,
      Date: "Total",
      [`Amount Received${currencyHeader}`]: Number(totalR.toFixed(2)),
      [`Total Expense${currencyHeader}`]: Number(totalE.toFixed(2)),
    });
    excelExportUtils(tableData, "Cash_Flow_Report", "Cash Flow");
  };

  return (
    <>
      <div className="page-body">
        <Breadcrumbs pageTitle="Cash Flow Report" parent="Reports" title="Cash Flow Report" />
        <Col xl={12} className="mb-lg-3">
          <Card>
            <CardBody>
              <Row className="align-items-center justify-content-between mb-2">
                <Col xl={2}>
                  <div className="d-flex gap-2">
                    {hasPermission("cash-flow-report.download_pdf") && (
                      <Button color="primary" onClick={exportPDF} className="d-flex justify-content-center align-items-center p-0" style={{ width: "30px", height: "30px" }} title="PDF">
                        <FaFilePdf size={16} />
                      </Button>
                    )}
                    {hasPermission("cash-flow-report.download_excel") && (
                      <Button color="secondary" onClick={exportExcel} className="d-flex justify-content-center align-items-center p-0" style={{ width: "30px", height: "30px" }} title="Excel">
                        <FaFileExcel size={16} />
                      </Button>
                    )}
                  </div>
                </Col>
                <Col xl={6}>
                  <div className="d-flex align-items-center gap-2 justify-content-end">
                    <label className="mb-0">From:</label>
                    <DatePicker
                      selected={fromDate}
                      onChange={(d) => { setFromDate(d); setCurrentPage(1); }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Start date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                      isClearable
                    />
                    <label className="mb-0">To:</label>
                    <DatePicker
                      selected={toDate}
                      onChange={(d) => { setToDate(d); setCurrentPage(1); }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="End date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                      isClearable
                    />
                  </div>
                </Col>
              </Row>
              <div className="table-responsive custom-scrollbar mt-3">
                <DataTable
                  data={columnsData}
                  columns={columns}
                  highlightOnHover
                  striped
                  customStyles={customStyles}
                  pagination
                  paginationServer
                  paginationPerPage={perPage}
                  paginationTotalRows={totalRows}
                  onChangePage={(p) => setCurrentPage(p)}
                  onChangeRowsPerPage={(np) => { setPerPage(np); setCurrentPage(1); }}
                  paginationDefaultPage={currentPage}
                  responsive
                  sortServer
                  onSort={handleSort}
                  conditionalRowStyles={conditionalRowStyles}
                  progressPending={tableLoading}
                  progressComponent={
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px", width: "100%" }}>
                      <Spinner color="primary" />
                    </div>
                  }
                />
              </div>
            </CardBody>
          </Card>
        </Col>
        <ToastContainer />
      </div>
    </>
  );
}
